// The tower defence's numbers in one place: what a tower costs and what its upgrades do, what a
// kill pays, what a leak costs the Command Center, and which of the buildings and units from
// data.ts the mode uses at all. Tuned by sim/scripts/td-tune.ts.
import { DEFAULT_CONFIG, type BuildingDef, type GameConfig, type Threat } from './data.ts';
import { TICKS_PER_SECOND } from './fixed.ts';

/** The towers, in the order the shop shows them: cheap and general first, the missile shield, then the stations. */
export const TOWERS: readonly string[] = [
  'mg_nest', 'sniper_nest', 'land_mine', 'bunker', 'at_mine', 'atgm_post', 'howitzer',
  'drone_interceptor', 'air_defence', 'c_ram', 'missile_defence', 'patriot', 'thaad',
  'tank_depot', 'helipad', 'airbase',
];

/** The patrol stations' units: which threats each roams after. */
export const PATROL_TARGETS: Record<string, readonly Threat[]> = {
  tank: ['infantry', 'vehicle'],
  attack_heli: ['infantry', 'vehicle', 'drone', 'aircraft'],
  f16: ['aircraft', 'drone', 'cruise'],
};

/**
 * The fleet: the enemy's ships hold station this far out to sea, in tiles west of the coast, and
 * launch missiles and drones at the Command Center from there. Nothing the player builds reaches
 * them; what they launch is the fight. They sail in from FLEET_APPROACH tiles further out at the
 * start of their wave, and back out once their launchers are empty.
 */
export const FLEET_STATION = 20;
export const FLEET_APPROACH = 10;
/** A ship's speed on the way in and out, tiles a second: the first launch comes a few seconds into the wave. */
export const FLEET_SPEED_TPS = 4;
/** A patrol unit shot down comes back from its station after this long. */
export const PATROL_RESPAWN_TICKS = 20 * TICKS_PER_SECOND;
/** The fastest a patrol moves, units a tick: a fighter at full speed would never turn. */
export const PATROL_MAX_SPEED = Math.floor((6 * 1024) / TICKS_PER_SECOND);

/** Cash to place each tower. */
export const PRICE: Record<string, number> = {
  mg_nest: 220, sniper_nest: 180, land_mine: 35, bunker: 350, at_mine: 70, atgm_post: 260, howitzer: 420,
  drone_interceptor: 170, air_defence: 320, c_ram: 450, missile_defence: 520, patriot: 750, thaad: 1000,
  tank_depot: 550, helipad: 650, airbase: 1400,
};

/** The enemy, in the order the waves bring them, and what each pays when it falls. */
export const REWARD: Record<string, number> = {
  conscript: 2, rifleman: 3, marksman: 5, heavy_gunner: 8, at_team: 10, tank: 40,
  scout_drone: 6, fpv_drone: 5, attack_drone: 10, shahed: 16, attack_heli: 55,
  cruise_missile: 30, ballistic: 60, hgv: 120,
  kalibr: 30,
  icbm: 250, hgv_nuclear: 180,
};

/** What a leak costs the Command Center's integrity: the warheads hurt the most, and a nuke nearly ends it. */
export const BREACH: Record<string, number> = {
  conscript: 1, rifleman: 1, marksman: 1, heavy_gunner: 2, at_team: 2, tank: 6,
  scout_drone: 1, fpv_drone: 1, attack_drone: 2, shahed: 4, attack_heli: 6,
  cruise_missile: 5, ballistic: 10, hgv: 12,
  kalibr: 5,
  icbm: 45, hgv_nuclear: 35,
};

export const MAX_TOWER_LEVEL = 3;

/** The upgrade's price as a share of the tower's price, by the level it goes to. */
const UPGRADE_PCT: Record<number, number> = { 2: 70, 3: 120 };
/** Damage and range gains by level, in percent over level 1. */
const DAMAGE_PCT: Record<number, number> = { 1: 0, 2: 35, 3: 80 };
const RANGE_PCT: Record<number, number> = { 1: 0, 2: 10, 3: 20 };
/** Sold towers give this much of what was spent on them back. */
export const SELL_PCT = 70;
/** A destroyed tower is rebuilt for this share of its price, back at its level. */
export const REBUILD_PCT = 50;

export function upgradePrice(type: string, toLevel: number): number {
  return Math.floor(((PRICE[type] ?? 0) * (UPGRADE_PCT[toLevel] ?? 0)) / 100);
}

/** All the cash put into a tower at this level. */
export function spentOn(type: string, level: number): number {
  let total = PRICE[type] ?? 0;
  for (let l = 2; l <= level; l++) total += upgradePrice(type, l);
  return total;
}

export function sellValue(type: string, level: number): number {
  return Math.floor((spentOn(type, level) * SELL_PCT) / 100);
}

export function rebuildPrice(type: string): number {
  return Math.floor(((PRICE[type] ?? 0) * REBUILD_PCT) / 100);
}

export function damageAt(def: BuildingDef, level: number): number {
  return Math.floor(((def.damage ?? 0) * (100 + (DAMAGE_PCT[level] ?? 0))) / 100);
}

export function rangeAt(def: BuildingDef, level: number): number {
  return Math.floor(((def.range ?? 0) * (100 + (RANGE_PCT[level] ?? 0))) / 100);
}

/** The enemy walks faster here than in a raid: a road is not a battlefield. In percent. */
export const ENEMY_SPEED_PCT = 150;

/** Cash paid when a wave is cleared: a little more each wave. */
export function waveBonus(n: number): number {
  return 100 + 15 * n;
}

/** The enemy's fire against towers, in percent of what it would do in a raid: a nuisance that adds up, not a wrecking ball. */
export const TOWER_DAMAGE_PCT = 25;

/**
 * What a kill pays by the wave it came with, in percent. Full pay throughout: a taper past the
 * hand-made waves was tried after a played game ended with cash to spare, and it starved the
 * missile shield instead (the self-play builder lost Switchback and Pincer on wave 27).
 */
export function rewardPct(_n: number): number {
  return 100;
}

/** Calling the next wave while one is still on pays extra, for the player in a hurry. */
export const EARLY_CALL_BONUS_PCT = 50;

/** A wave is over this long after its last enemy is dead or through. */
export const WAVE_QUIET_TICKS = 2 * TICKS_PER_SECOND;

/** The game's rules: the raids' config as it stands; only the buildings in TOWERS and `hq` matter. */
export const TD_CONFIG: GameConfig = DEFAULT_CONFIG;
